import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loading01Icon, SearchLgIcon } from '@untitledui-icons/react/line'

import { usePlaceAutocomplete, type PlacePrediction } from '@/hooks/api'
import { cn } from '@/lib/utils'

const AUTOCOMPLETE_DEBOUNCE_MS = 250
const MIN_QUERY_LENGTH = 3

/**
 * The address part of a prediction. Google returns the venue name as
 * `main_text` and the street/city as `secondary_text`, which is exactly what
 * the picker should show — so no place-details request is needed.
 */
function addressOf(prediction: PlacePrediction): string {
  return prediction.structured_formatting?.secondary_text || prediction.description
}

interface WidgetVenueAddressFieldProps {
  label: string
  value: string | null
  /** Called on selection, blur, and Enter — only when the address changed. */
  onSave: (address: string | null) => void
  isSaving?: boolean
}

/** Address search for one venue, saved as you go like the assignment chips. */
export function WidgetVenueAddressField({
  label,
  value,
  onSave,
  isSaving,
}: WidgetVenueAddressFieldProps) {
  const { t } = useTranslation()
  const base = 'resources.widgets.multi_venue'

  const containerRef = useRef<HTMLDivElement>(null)
  // Typed text outlives a failed save, so the address is never silently lost.
  const [draft, setDraft] = useState(value ?? '')
  const [query, setQuery] = useState('')
  const [isOpen, setOpen] = useState(false)

  useEffect(() => {
    const timeoutId = window.setTimeout(() => setQuery(draft.trim()), AUTOCOMPLETE_DEBOUNCE_MS)
    return () => window.clearTimeout(timeoutId)
  }, [draft])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const { data: predictions = [], isFetching } = usePlaceAutocomplete(
    query,
    isOpen && query.length >= MIN_QUERY_LENGTH
  )

  const save = useCallback(
    (next: string) => {
      const trimmed = next.trim()
      if (trimmed === (value ?? '')) return
      onSave(trimmed || null)
    },
    [onSave, value]
  )

  const handleSelect = (prediction: PlacePrediction) => {
    const address = addressOf(prediction)
    setDraft(address)
    setOpen(false)
    save(address)
  }

  return (
    <div ref={containerRef} className="relative flex flex-col gap-1.5">
      <p className="text-[13px] leading-[18px] font-medium text-gray-900 dark:text-gray-100">
        {label}
      </p>

      <div className="relative">
        <SearchLgIcon className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-gray-500" />
        <input
          type="text"
          autoComplete="off"
          value={draft}
          placeholder={t(`${base}.address_placeholder`)}
          aria-label={t(`${base}.address_label`, { venue: label })}
          onChange={(e) => {
            setDraft(e.target.value)
            setOpen(true)
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => save(draft)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              setOpen(false)
              save(draft)
            }
            if (e.key === 'Escape') {
              setOpen(false)
            }
          }}
          className={cn(
            'h-10 w-full rounded-md border border-gray-200 bg-white pr-10 pl-9 text-sm shadow-xs',
            'placeholder:text-gray-400 focus:border-gray-400 focus:outline-none',
            'dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:focus:border-gray-500'
          )}
        />
        {(isFetching || isSaving) && (
          <Loading01Icon className="absolute top-1/2 right-3 size-4 -translate-y-1/2 animate-spin text-gray-400" />
        )}
      </div>

      {isOpen && predictions.length > 0 && (
        <div className="absolute top-full right-0 left-0 z-50 mt-1 max-h-60 overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-900">
          {predictions.map((prediction) => (
            <button
              key={prediction.place_id}
              type="button"
              // Blur would close the list before the click lands.
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleSelect(prediction)}
              className="block w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <span className="block font-medium text-gray-900 dark:text-gray-100">
                {prediction.structured_formatting?.main_text ?? prediction.description}
              </span>
              <span className="block text-gray-500 dark:text-gray-400">
                {addressOf(prediction)}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
